import type { TRIMaximumChemicalAmountCodeType } from '../../types/form-r.ts';
import type { Section4MaxOnSiteAmount } from '../../types/questionnaires.ts';
import type { Warning } from '../../types/tri.ts';
import type { CasOrCategory, ProductID } from '../../types/db.ts';
import { Gram, Pound } from '../formulas/units.ts';

/** Convert pounds to the EPA range code. */
export function amountToRangeCode(casOrCategory: CasOrCategory, amount: number): TRIMaximumChemicalAmountCodeType {
  if (casOrCategory === 'N150') {
    if (amount < 0.1) return '12';
    else if (amount < 1) return '13';
    else if (amount < 10) return '14';
    else if (amount < 100) return '15';
    else if (amount < 1000) return '16';
    else if (amount < 10000) return '17';
    else if (amount < 100000) return '18';
    else if (amount < 1000000) return '19';
    else return '20';
  } else {
    if (amount < 100) return '01';
    else if (amount < 1000) return '02';
    else if (amount < 10000) return '03';
    else if (amount < 100000) return '04';
    else if (amount < 1000000) return '05';
    else if (amount < 10000000) return '06';
    else if (amount < 50000000) return '07';
    else if (amount < 100000000) return '08';
    else if (amount < 500000000) return '09';
    else if (amount < 1000000000) return '10';
    else return '11';
  }
}

/** Product info needed for per-product-max and per-product-backlog calculations. */
export interface Section4ProductInfo {
  productID: ProductID;
  productName: string;
  /** Concentration of this chemical in this product (0..1). */
  concentration: number;
  /** Pounds per usage unit (already incorporates specific gravity for volume units). */
  poundsPerUsageUnit: number;
  /** Total pounds of this product used during the reporting year. */
  totalPoundsUsed: number;
}

/**
 * Convert a Section4MaxOnSiteAmount to a TRI range code.
 */
export function formRMaximumChemicalAmountCode(
  casOrCategory: CasOrCategory,
  totalAmountUsed: { pounds: number; grams: number },
  answer: Section4MaxOnSiteAmount,
  productInfoForChemical: Section4ProductInfo[],
  warnings: Warning[],
): TRIMaximumChemicalAmountCodeType {
  switch (answer.method) {
    case 'inventory-backlog-facility-wide':
      return section4InventoryBacklogCalc(
        casOrCategory,
        totalAmountUsed,
        answer.inventoryBacklogInWeeksOnSite,
        warnings,
      );

    case 'inventory-backlog-per-product': {
      const backlogEntries = answer.perProductBacklogWeeks;
      let totalChemicalPounds = 0;

      for (const productInfo of productInfoForChemical) {
        let weeks = backlogEntries.find((e) => e.productID === productInfo.productID)?.backlogWeeks;
        if (typeof weeks !== 'number') {
          weeks = 52;
          warnings.push({
            warningID: 'TW1111',
            type: 'questionnaire',
            subType: 'maxOnSiteAmount',
            casOrCategory,
            message: `No per-product backlog weeks provided. Assuming full year inventory on-site.`,
            blocksPriorYearSection8Calculation: false,
          });
        }

        totalChemicalPounds += (weeks / 52) * productInfo.totalPoundsUsed * productInfo.concentration;
      }

      if (casOrCategory === 'N150') {
        return amountToRangeCode(casOrCategory, Pound.convertTo(Gram, totalChemicalPounds) as number);
      } else {
        return amountToRangeCode(casOrCategory, totalChemicalPounds);
      }
    }

    case 'direct-entry': {
      const peakPounds = answer.chemicalPeakPounds[casOrCategory];
      if (typeof peakPounds !== 'number') {
        warnings.push({
          warningID: 'TW1112',
          type: 'questionnaire',
          subType: 'maxOnSiteAmount',
          casOrCategory,
          message: `No peak ${casOrCategory === 'N150' ? 'grams' : 'pounds'} entered for tank/container capacity method. Assuming full year inventory on-site.`,
          blocksPriorYearSection8Calculation: false,
        });
        return section4InventoryBacklogCalc(casOrCategory, totalAmountUsed, 52, warnings);
      } else {
        if (casOrCategory === 'N150') {
          return amountToRangeCode(casOrCategory, Pound.convertTo(Gram, peakPounds) as number);
        } else {
          return amountToRangeCode(casOrCategory, peakPounds);
        }
      }
    }

    case 'per-product-max': {
      let totalChemicalPounds = 0;

      for (const productInfo of productInfoForChemical) {
        let maxQuantityOnSite = answer.perProductMaxQuantities.find(
          (e) => e.productID === productInfo.productID,
        )?.maxQuantityOnSite;
        let poundsPerUsageUnit = productInfo.poundsPerUsageUnit;
        if (typeof maxQuantityOnSite !== 'number') {
          warnings.push({
            warningID: 'TW1113',
            type: 'questionnaire',
            subType: 'maxOnSiteAmount',
            casOrCategory,
            message: `No per-product maximum quantities provided. Using full usage pounds for Section 4.`,
            blocksPriorYearSection8Calculation: false,
          });
          maxQuantityOnSite = productInfo.totalPoundsUsed;
          poundsPerUsageUnit = 1;
        }

        totalChemicalPounds += maxQuantityOnSite * poundsPerUsageUnit * productInfo.concentration;
      }

      if (casOrCategory === 'N150') {
        return amountToRangeCode(casOrCategory, Pound.convertTo(Gram, totalChemicalPounds) as number);
      } else {
        return amountToRangeCode(casOrCategory, totalChemicalPounds);
      }
    }

    case 'direct-override': {
      const code = answer.chemicalOverrideRangeCodes[casOrCategory];
      if (code) {
        return code;
      }

      warnings.push({
        warningID: 'TW1114',
        type: 'questionnaire',
        subType: 'maxOnSiteAmount',
        casOrCategory,
        message: `Direct override selected but no range code provided. Assuming full year inventory on-site.`,
        blocksPriorYearSection8Calculation: false,
      });
      return section4InventoryBacklogCalc(casOrCategory, totalAmountUsed, 52, warnings);
    }

    default: {
      const unexpected: never = answer.method;
      throw new Error(`Unknown Section 4 method: ${unexpected}`);
    }
  }
}

/**
 * Weeks-based inventory-backlog calculation: (weeks / 52) × totalPoundsUsed → range code.
 */
function section4InventoryBacklogCalc(
  casOrCategory: CasOrCategory,
  totalAmountUsed: { pounds: number; grams: number },
  inventoryBacklogInWeeksOnSite: '' | number,
  warnings: Warning[],
): TRIMaximumChemicalAmountCodeType {
  const inventoryBacklogWeeksOnSite = inventoryBacklogInWeeksOnSite === '' ? 52 : inventoryBacklogInWeeksOnSite;
  if (inventoryBacklogInWeeksOnSite === '') {
    warnings.push({
      warningID: 'TW1007',
      type: 'questionnaire',
      subType: 'maxOnSiteAmount',
      casOrCategory: null,
      message: `No inventory backlog answer provided. Assuming full year inventory on-site.`,
      blocksPriorYearSection8Calculation: false,
    });
  }
  const percentageOnSite = inventoryBacklogWeeksOnSite / 52;
  const amount = casOrCategory === 'N150' ? totalAmountUsed.grams : totalAmountUsed.pounds;
  const maxChemicalPoundsOnSite = percentageOnSite * amount;
  return amountToRangeCode(casOrCategory, maxChemicalPoundsOnSite);
}
